import { SchemaType } from '../constants/schema.js'
import { sanitizeCardDescription } from './sanitize.js'
import { computeGridPositions } from './diagramLayout.js'
import { DomainModelRole } from '../constants/cardTypes.js'

// Identifier baked into every exported workflow spec. Points at the current Continuum app domain
// where public/schemas/domain-model/v1.json is served.
// TODO(prod-domain): update to the final Continuum production FQDN once one is provisioned
// (continuumdesign.com is taken) — exported artifacts carry this URL, so keep it stable per release.
export const DOMAIN_MODEL_SCHEMA_URL = 'https://continuum-design-doc.aoi-labs.com/schemas/domain-model/v1.json'

export const toPascalCase = (name) => {
  if (!name) {
    return ''
  }

  return name
    .split(/[^A-Za-z0-9]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join('')
}

export const makeRef = (path) => ({ $ref: path })

export const resolveRefPath = (refPath, root) => {
  if (!refPath || !refPath.startsWith('#/')) {
    return undefined
  }
  const segments = refPath.slice(2).split('/')
  let current = root
  for (const segment of segments) {
    if (current == null) {
      return undefined
    }
    current = current[segment]
  }

  return current
}

const makeUniqueKey = (key, existingKeysSet) => {
  if (!existingKeysSet.has(key)) {
    return key
  }
  let suffix = 2
  while (existingKeysSet.has(`${key}${suffix}`)) {
    suffix++
  }

  return `${key}${suffix}`
}

const arrayToMap = (arr, nameField, { existingKeys = new Set(), keepName = false } = {}) => {
  const map = {}
  for (const item of arr) {
    const rawKey = toPascalCase(item[nameField])
    const key = makeUniqueKey(rawKey, existingKeys)
    existingKeys.add(key)
    if (keepName) {
      map[key] = item
    } else {
      const { [nameField]: _omitted, ...rest } = item
      map[key] = rest
    }
  }

  return map
}

const convertSchemasToMaps = (schemas) => {
  if (!schemas) {
    return schemas
  }
  const result = {}
  if (schemas.commands) {
    result.commands = arrayToMap(schemas.commands, 'name')
  }
  if (schemas.queries) {
    result.queries = arrayToMap(schemas.queries, 'name')
  }
  if (schemas.entities) {
    result.entities = arrayToMap(schemas.entities, 'name')
  }
  if (schemas.domainEvents) {
    result.domainEvents = arrayToMap(schemas.domainEvents, 'name')
  }
  if (schemas.valueObjects) {
    result.valueObjects = arrayToMap(schemas.valueObjects, 'name')
  }

  return Object.keys(result).length > 0 ? result : undefined
}

/**
 * Build display-name → $ref-path lookups from the ARRAY form (before map conversion).
 * Maps original display names (e.g. "Create Cart") to their future $ref paths
 * (e.g. "#/schemas/commands/CreateCart").
 */
const buildNameToRefLookups = (result) => {
  const commandRefs = new Map()
  const queryRefs = new Map()
  const entityRefs = new Map()
  const domainEventSchemaRefs = new Map()
  const valueObjectRefs = new Map()
  const eventRefs = new Map()
  const eventIdRefs = new Map()

  const registerSchemas = (schemasArr, pathPrefix) => {
    if (!schemasArr) {
      return
    }
    for (const s of schemasArr.commands || []) {
      commandRefs.set(s.name, `${pathPrefix}/schemas/commands/${toPascalCase(s.name)}`)
    }
    for (const s of schemasArr.queries || []) {
      queryRefs.set(s.name, `${pathPrefix}/schemas/queries/${toPascalCase(s.name)}`)
    }
    for (const s of schemasArr.entities || []) {
      entityRefs.set(s.name, `${pathPrefix}/schemas/entities/${toPascalCase(s.name)}`)
    }
    for (const s of schemasArr.domainEvents || []) {
      domainEventSchemaRefs.set(s.name, `${pathPrefix}/schemas/domainEvents/${toPascalCase(s.name)}`)
    }
    for (const s of schemasArr.valueObjects || []) {
      valueObjectRefs.set(s.name, `${pathPrefix}/schemas/valueObjects/${toPascalCase(s.name)}`)
    }
  }

  // Assign suffix-aware slugs per BC, matching how arrayToMap will key the
  // events later. Track by event id so ref lookups stay precise even when
  // multiple events share the same description.
  const registerEvents = (eventsArr, pathPrefix) => {
    const existingKeys = new Set()
    for (const e of eventsArr || []) {
      const slug = makeUniqueKey(toPascalCase(e.event), existingKeys)
      existingKeys.add(slug)
      const path = `${pathPrefix}/domainEvents/${slug}`
      if (e._id) {
        eventIdRefs.set(e._id, path)
      }
      // Description-keyed fallback for legacy callers; first occurrence wins
      // so the non-suffixed slug stays mapped to the first event.
      if (!eventRefs.has(e.event)) {
        eventRefs.set(e.event, path)
      }
    }
  }

  registerSchemas(result.schemas, '#')
  registerEvents(result.domainEvents, '#')

  for (const bc of result.externalBoundedContexts || []) {
    const bcKey = toPascalCase(bc.name)
    const prefix = `#/externalBoundedContexts/${bcKey}`
    registerSchemas(bc.schemas, prefix)
    registerEvents(bc.domainEvents, prefix)
  }

  return { commandRefs, queryRefs, entityRefs, domainEventSchemaRefs, valueObjectRefs, eventRefs, eventIdRefs }
}

const replaceRefsInEvents = (events, lookups) => {
  if (!events) {
    return
  }
  for (const event of events) {
    // command → $ref (drop if unresolvable)
    if (event.command && typeof event.command === 'string') {
      const refPath = lookups.commandRefs.get(event.command)
      if (refPath) {
        event.command = makeRef(refPath)
      } else {
        delete event.command
      }
    }

    // domainEventSchema → $ref with description (drop if unresolvable)
    if (event.domainEventSchema?.name) {
      const refPath = lookups.domainEventSchemaRefs.get(event.domainEventSchema.name)
      if (refPath) {
        const refObj = makeRef(refPath)
        if (event.domainEventSchema.description) {
          refObj.description = event.domainEventSchema.description
        }
        event.domainEventSchema = refObj
      } else {
        delete event.domainEventSchema
      }
    }

    // aggregateRoot → $ref (check entities and valueObjects, drop if unresolvable)
    if (event.aggregateRoot && typeof event.aggregateRoot === 'string') {
      const refPath = lookups.entityRefs.get(event.aggregateRoot) || lookups.valueObjectRefs.get(event.aggregateRoot)
      if (refPath) {
        event.aggregateRoot = makeRef(refPath)
      } else {
        delete event.aggregateRoot
      }
    }

    // readModels → $ref + array sibling (drop entries without a resolvable ref)
    if (event.readModels) {
      event.readModels = event.readModels
        .map((rm) => {
          const name = rm.name
          if (!name) {
            return null
          }
          const refPath = lookups.queryRefs.get(name) || lookups.domainEventSchemaRefs.get(name)
          if (refPath) {
            const refObj = makeRef(refPath)
            if (rm.array) {
              refObj.array = true
            }
            if (rm.description) {
              refObj.description = rm.description
            }

            return refObj
          }

          return null
        })
        .filter(Boolean)
      if (event.readModels.length === 0) {
        delete event.readModels
      }
    }

    // follows → $ref for event names (keep "start", drop unresolvable).
    // Prefer event-id lookup so multi-named events resolve to their precise slug.
    if (event.follows) {
      const followIds = event._followsIds || []
      event.follows = event.follows
        .map((f, idx) => {
          if (f === 'start') {
            return f
          }
          const id = followIds[idx]
          const refPath = (id && lookups.eventIdRefs.get(id)) || lookups.eventRefs.get(f)
          if (refPath) {
            return makeRef(refPath)
          }

          return null
        })
        .filter(Boolean)
      if (event.follows.length === 0) {
        delete event.follows
      }
    }
    delete event._followsIds

    // conditions[].after → $ref for non-gateway events, kept as a plain
    // string when it references another decision's description (chained
    // gateways), preserved as "start" when it is the literal start marker.
    if (event.conditions) {
      for (const cond of event.conditions) {
        if (cond._emptyIs) {
          cond.is = ''
          delete cond._emptyIs
        }
        if (!cond.after || cond.after === 'start') {
          continue
        }
        const id = cond._afterEventId
        const refPath = (id && lookups.eventIdRefs.get(id)) || lookups.eventRefs.get(cond.after)
        if (refPath) {
          cond.after = makeRef(refPath)
        }
        delete cond._afterEventId
      }
      if (event.conditions.length === 0) {
        delete event.conditions
      }
    }

    delete event._id
  }
}

const replaceRefsInSchemas = (schemas, lookups) => {
  if (!schemas) {
    return
  }

  const replaceFieldRefs = (fields) => {
    if (!fields) {
      return
    }
    for (const field of fields) {
      if (field.relatedEntity && typeof field.relatedEntity === 'string') {
        const refPath = lookups.entityRefs.get(field.relatedEntity) || lookups.valueObjectRefs.get(field.relatedEntity)
        if (refPath) {
          field.relatedEntity = makeRef(refPath)
        } else {
          delete field.relatedEntity
        }
      }
      if (field.fields) {
        replaceFieldRefs(field.fields)
      }
    }
  }

  for (const schema of schemas.commands || []) {
    replaceFieldRefs(schema.fields)
  }
  for (const schema of schemas.queries || []) {
    if (schema.entity && typeof schema.entity === 'string') {
      const refPath = lookups.entityRefs.get(schema.entity)
      if (refPath) {
        schema.entity = makeRef(refPath)
      } else {
        delete schema.entity
      }
    }
    replaceFieldRefs(schema.fields)
  }
  for (const schema of schemas.domainEvents || []) {
    if (schema.entity && typeof schema.entity === 'string') {
      const refPath = lookups.entityRefs.get(schema.entity) || lookups.valueObjectRefs.get(schema.entity)
      if (refPath) {
        schema.entity = makeRef(refPath)
      } else {
        delete schema.entity
      }
    }
    replaceFieldRefs(schema.fields)
  }
  for (const schema of schemas.entities || []) {
    replaceFieldRefs(schema.fields)
  }
  for (const schema of schemas.valueObjects || []) {
    replaceFieldRefs(schema.fields)
  }
}

// Assigns a stable PascalCase slug to each decision entry (with collision
// suffix when descriptions repeat) and builds gatewayId → slug for resolving
// chained references to their precise parent record.
const assignDecisionSlugs = (decisions) => {
  if (!decisions || decisions.length === 0) {
    return { slugByEntry: [], gatewayIdToSlug: new Map() }
  }
  const existingKeys = new Set()
  const slugByEntry = []
  const gatewayIdToSlug = new Map()

  for (const decision of decisions) {
    const rawKey = toPascalCase(decision.description)
    const slug = makeUniqueKey(rawKey, existingKeys)
    existingKeys.add(slug)
    slugByEntry.push(slug)
    if (decision._gatewayId && !gatewayIdToSlug.has(decision._gatewayId)) {
      gatewayIdToSlug.set(decision._gatewayId, slug)
    }
  }

  return { slugByEntry, gatewayIdToSlug }
}

const replaceRefsInDecisions = (decisions, lookups, gatewayIdToSlug) => {
  if (!decisions) {
    return
  }
  for (const decision of decisions) {
    const parentGwId = decision._parentGatewayId
    const parentEventId = decision._followsEventId
    if (parentGwId && gatewayIdToSlug.has(parentGwId)) {
      decision.follows = makeRef(`#/decisions/${gatewayIdToSlug.get(parentGwId)}`)
    } else if (parentEventId && lookups.eventIdRefs.get(parentEventId)) {
      decision.follows = makeRef(lookups.eventIdRefs.get(parentEventId))
    } else if (decision.follows && decision.follows !== 'start') {
      const refPath = lookups.eventRefs.get(decision.follows)
      if (refPath) {
        decision.follows = makeRef(refPath)
      }
    }
    if (decision._emptyConditionLabel) {
      decision.conditionLabel = ''
      delete decision._emptyConditionLabel
    }
    delete decision._gatewayId
    delete decision._parentGatewayId
    delete decision._followsEventId
  }
}

const replaceDecisionRefsInConditions = (events, gatewayIdToSlug) => {
  if (!events) {
    return
  }
  for (const event of events) {
    for (const cond of event.conditions || []) {
      const gwId = cond._afterGatewayId
      if (gwId && gatewayIdToSlug.has(gwId)) {
        cond.after = makeRef(`#/decisions/${gatewayIdToSlug.get(gwId)}`)
      }
      delete cond._afterGatewayId
    }
  }
}

const decisionsArrayToMap = (decisions, slugByEntry) => {
  const map = {}
  decisions.forEach((decision, idx) => {
    map[slugByEntry[idx]] = decision
  })

  return map
}

const postProcessExport = (result, positionsById = null) => {
  // Build display-name → $ref-path lookups BEFORE converting to maps
  const lookups = buildNameToRefLookups(result)

  const { slugByEntry, gatewayIdToSlug } = assignDecisionSlugs(result.decisions || [])

  // Stamp positions onto events/decisions via their internal `_id`/`_gatewayId`, before the steps
  // below strip those markers. Opt-in (positionsById) — callers that omit it are unaffected.
  if (positionsById) {
    for (const event of result.domainEvents || []) {
      if (positionsById[event._id]) {
        event.position = positionsById[event._id]
      }
    }
    for (const decision of result.decisions || []) {
      if (positionsById[decision._gatewayId]) {
        decision.position = positionsById[decision._gatewayId]
      }
    }
    for (const bc of result.externalBoundedContexts || []) {
      for (const event of bc.domainEvents || []) {
        if (positionsById[event._id]) {
          event.position = positionsById[event._id]
        }
      }
    }
  }

  // Replace string references with $ref objects (while still in array form)
  replaceRefsInEvents(result.domainEvents || [], lookups)
  replaceRefsInSchemas(result.schemas, lookups)
  replaceRefsInDecisions(result.decisions, lookups, gatewayIdToSlug)
  replaceDecisionRefsInConditions(result.domainEvents || [], gatewayIdToSlug)
  for (const bc of result.externalBoundedContexts || []) {
    replaceRefsInEvents(bc.domainEvents || [], lookups)
    replaceRefsInSchemas(bc.schemas, lookups)
    replaceDecisionRefsInConditions(bc.domainEvents || [], gatewayIdToSlug)
  }

  if (result.decisions) {
    result.decisions = decisionsArrayToMap(result.decisions, slugByEntry)
  }

  // Convert domainEvents array → map (keep 'event' display name)
  if (result.domainEvents) {
    result.domainEvents = arrayToMap(result.domainEvents, 'event', { keepName: true })
  }

  // Convert schemas arrays → maps (strip 'name', key serves as identifier)
  result.schemas = convertSchemasToMaps(result.schemas)

  // Convert externalBoundedContexts array → map
  if (result.externalBoundedContexts) {
    const bcMap = {}
    const bcKeys = new Set()
    for (const bc of result.externalBoundedContexts) {
      const rawKey = toPascalCase(bc.name)
      const key = makeUniqueKey(rawKey, bcKeys)
      bcKeys.add(key)
      const { name: _omitted, ...rest } = bc
      if (rest.domainEvents) {
        rest.domainEvents = arrayToMap(rest.domainEvents, 'event', { keepName: true })
      }
      rest.schemas = convertSchemasToMaps(rest.schemas)
      bcMap[key] = rest
    }
    result.externalBoundedContexts = bcMap
  }
}

export const omitEmpty = (obj) => {
  if (obj === null || obj === undefined) {
    return undefined
  }

  if (Array.isArray(obj)) {
    return obj.map(omitEmpty).filter((item) => item !== undefined)
  }

  if (typeof obj === 'object') {
    const cleaned = {}

    for (const [key, value] of Object.entries(obj)) {
      const cleanedValue = omitEmpty(value)
      if (cleanedValue !== undefined) {
        cleaned[key] = cleanedValue
      }
    }

    return Object.keys(cleaned).length > 0 ? cleaned : undefined
  }

  if (obj === '') {
    return undefined
  }

  return obj
}

const HTML_ENTITY_MAP = {
  '&lt;': '<',
  '&gt;': '>',
  '&amp;': '&',
  '&quot;': '"',
  '&#39;': '\'',
  '&nbsp;': ' '
}

export const decodeHtmlEntities = (str) => {
  if (!str) {
    return str
  }

  return str.replace(/&(?:lt|gt|amp|quot|#39|nbsp);/g, (match) => HTML_ENTITY_MAP[match])
}

/**
 * Build lookup maps for resolving UUIDs to human-readable names.
 */
export const buildExportContext = (workflow, cardTypesMap) => {
  const lanes = workflow.lanes || []
  const groups = workflow.groups || []
  const schemas = workflow.schemas || []
  const domainEvents = workflow.domainEvents || []
  const boundedContexts = workflow.boundedContexts || []

  const laneMap = new Map(lanes.map((lane) => [lane.id, lane.name]))
  const groupMap = new Map(groups.map((group) => [group.id, group.name]))
  const bcMap = new Map(boundedContexts.map((bc) => [bc.id, bc.name]))
  const entityMap = new Map(
    schemas.filter((schema) => schema.type === SchemaType.ENTITY).map((schema) => [schema.id, schema.name])
  )
  const domainEventMap = new Map(
    schemas.filter((schema) => schema.type === SchemaType.DOMAIN_EVENT).map((schema) => [schema.id, schema.name])
  )
  const eventMap = new Map(domainEvents.map((event) => [event.id, event.description]))

  const gatewayMap = new Map()
  for (const event of domainEvents) {
    if (event.type === 'bpmn:ExclusiveGateway') {
      gatewayMap.set(event.id, {
        description: event.description,
        parents: event.parents || [],
        conditionLabels: event.conditionLabels || [],
        laneId: event.laneId,
        cards: event.cards || []
      })
    }
  }

  // Build schema name map:
  // - Entity, Query, and DomainEvent schemas use their own name (managed on dedicated pages)
  // - Command schemas derive name from card description (no dedicated page yet)
  const schemaNameMap = new Map()

  for (const schema of schemas) {
    if (schema.type === SchemaType.ENTITY || schema.type === SchemaType.QUERY || schema.type === SchemaType.DOMAIN_EVENT) {
      schemaNameMap.set(schema.id, schema.name)
    }
  }

  for (const event of domainEvents) {
    for (const card of event.cards || []) {
      const role = cardTypesMap[card.cardTypeId]?.domainModelRole
      if (card.schemaId && !schemaNameMap.has(card.schemaId)) {
        if (role === DomainModelRole.COMMAND) {
          schemaNameMap.set(card.schemaId, sanitizeCardDescription(card.description || ''))
        }
      }
    }
  }

  // Fallback for schemas not referenced by any card
  for (const schema of schemas) {
    if (!schemaNameMap.has(schema.id)) {
      schemaNameMap.set(schema.id, schema.name)
    }
  }

  return { laneMap, groupMap, bcMap, entityMap, domainEventMap, eventMap, schemaNameMap, gatewayMap }
}

export const resolveSchema = (ref, workflow, cardTypesMap = null) => {
  const segments = ref.replace('#/', '').split('/')

  const schemaIndex = segments.indexOf('schemas')
  if (schemaIndex === -1 || schemaIndex + 2 >= segments.length) {
    return null
  }

  const category = segments[schemaIndex + 1]
  const pascalKey = segments[schemaIndex + 2]

  const schemas = workflow.schemas || []

  if (category === 'entities' || category === 'valueObjects') {
    return schemas.find((schema) => schema.type === SchemaType.ENTITY && toPascalCase(schema.name) === pascalKey) || null
  }

  if (category === 'domainEvents') {
    return schemas.find((schema) => schema.type === SchemaType.DOMAIN_EVENT && toPascalCase(schema.name) === pascalKey) || null
  }

  if ((category === 'commands' || category === 'queries') && cardTypesMap) {
    const context = buildExportContext(workflow, cardTypesMap)
    const expectedType = category === 'commands' ? 'Command' : 'Query'

    return schemas.find((schema) => {
      if (schema.type !== expectedType) {
        return false
      }
      const exportedName = context.schemaNameMap.get(schema.id) || schema.name

      return toPascalCase(exportedName) === pascalKey
    }) || null
  }

  return null
}

export const resolveDomainEvent = (ref, workflow) => {
  if (!ref) {
    return null
  }
  const segments = ref.replace('#/', '').split('/')
  const eventIndex = segments.indexOf('domainEvents')
  if (eventIndex === -1 || eventIndex + 1 >= segments.length) {
    return null
  }
  const targetSlug = segments[eventIndex + 1]
  const domainEvents = workflow.domainEvents || []

  // Rebuild slugs the same way the export does: walk task events in order,
  // turn each description into PascalCase, and add `2`, `3`, ... when the
  // same slug repeats. Matches the suffixed paths in get_workflow output.
  const existingKeys = new Set()
  for (const event of domainEvents) {
    if (event.type === 'bpmn:ExclusiveGateway') {
      continue
    }
    const slug = makeUniqueKey(toPascalCase(event.description), existingKeys)
    existingKeys.add(slug)
    if (slug === targetSlug) {
      return event
    }
  }

  return null
}

export const resolveDecision = (ref, workflow) => {
  if (!ref) {
    return null
  }
  const segments = ref.replace('#/', '').split('/')
  const idx = segments.indexOf('decisions')
  if (idx === -1 || idx + 1 >= segments.length) {
    return null
  }
  const targetSlug = segments[idx + 1]
  const domainEvents = workflow.domainEvents || []

  // Rebuild slugs the same way the export does: walk gateways in order,
  // turn each description into PascalCase, and add `2`, `3`, ... when the
  // same slug repeats. Multi-parent gateways take one slug per parent.
  const existingKeys = new Set()
  for (const event of domainEvents) {
    if (event.type !== 'bpmn:ExclusiveGateway') {
      continue
    }
    const base = toPascalCase(event.description)
    const parentCount = Math.max(1, (event.parents || []).length)
    for (let i = 0; i < parentCount; i++) {
      let slug = base
      let n = 2
      while (existingKeys.has(slug)) {
        slug = `${base}${n++}`
      }
      existingKeys.add(slug)
      if (slug === targetSlug) {
        return event
      }
    }
  }

  return null
}

export const resolveDomainEventOrDecision = (ref, workflow) => {
  if (!ref) {
    return null
  }
  if (ref.includes('/decisions/')) {
    return resolveDecision(ref, workflow)
  }

  return resolveDomainEvent(ref, workflow)
}

export const resolveLane = (name, workflow) => {
  if (!name) {
    return null
  }

  return (workflow.lanes || []).find((lane) => lane.name === name) || null
}

export const cleanNestedFields = (nestedFields, entityMap) => {
  if (!nestedFields || !Array.isArray(nestedFields) || nestedFields.length === 0) {
    return undefined
  }

  return nestedFields.map((f) => {
    const cleaned = { name: f.name }
    if (f.cardinality === 'one-to-many') {
      cleaned.array = true
    }
    if (f.relatedEntityId && entityMap) {
      const ref = entityMap.get(f.relatedEntityId)
      if (ref) {
        cleaned.relatedEntity = ref
      }
    }
    if (f.computed) {
      cleaned.computed = true
    }
    if (f.type === 'filter') {
      cleaned.isFilter = true
    }
    const nested = cleanNestedFields(f.fields, entityMap)
    if (nested) {
      const requiredNames = (f.fields || [])
        .filter((child) => child.tags?.includes('REQUIRED'))
        .map((child) => child.name)
      if (requiredNames.length > 0) {
        cleaned.required = requiredNames
      }
      cleaned.fields = nested
    }
    return cleaned
  })
}

export const cleanupDomainEvent = (event, cardTypesMap, context) => {
  const cards = event.cards || []

  const {
    commandCard,
    aggregateCard,
    domainEventCard,
    readModelCards,
    gwtCards,
    businessRuleCards
  } = cards.reduce((acc, c) => {
    switch (cardTypesMap[c.cardTypeId]?.domainModelRole) {
      case DomainModelRole.COMMAND:
        acc.commandCard = acc.commandCard || c
        break
      case DomainModelRole.AGGREGATE_ROOT:
        acc.aggregateCard = acc.aggregateCard || c
        break
      case DomainModelRole.DOMAIN_EVENT:
        acc.domainEventCard = acc.domainEventCard || c
        break
      case DomainModelRole.READ_MODEL:
        acc.readModelCards.push(c)
        break
      case DomainModelRole.GIVEN_WHEN_THEN:
        acc.gwtCards.push(c)
        break
      default:
        acc.businessRuleCards.push(c)
    }

    return acc
  }, {
    commandCard: null,
    aggregateCard: null,
    domainEventCard: null,
    readModelCards: [],
    gwtCards: [],
    businessRuleCards: []
  })

  const readModels = readModelCards
    .map((card) => {
      const resolvedId = card.schemaId || context.virtualSchemaMap?.get(card.id)
      const schemaName = context.schemaNameMap.get(resolvedId)

      return omitEmpty({
        name: schemaName,
        description: sanitizeCardDescription(card.description || '') || undefined
      })
    })
    .filter(Boolean)

  const customCards = businessRuleCards
    .map((c) => {
      const cardType = cardTypesMap[c.cardTypeId]

      return omitEmpty({
        type: cardType?.title,
        description: decodeHtmlEntities(sanitizeCardDescription(c.description || ''))
      })
    })
    .filter(Boolean)

  const acceptanceCriteria = gwtCards.length > 0
    ? gwtCards.map((c) => decodeHtmlEntities(sanitizeCardDescription(c.description || ''))).filter(Boolean)
    : undefined

  const resolvedFollows = []
  const resolvedFollowIds = []
  const conditions = []

  const pushFollow = (parentId, name) => {
    if (parentId === 'start') {
      if (!resolvedFollows.includes('start')) {
        resolvedFollows.push('start')
        resolvedFollowIds.push(null)
      }

      return
    }
    if (!resolvedFollowIds.includes(parentId)) {
      resolvedFollows.push(name)
      resolvedFollowIds.push(parentId)
    }
  }

  // Maps a conditionLabels entry to the `is` branch label: present → its name
  // (cleared/whitespace-only → ""), absent → undefined (omits `is`, import shows "[Label]").
  const edgeLabelFrom = (entry) => {
    if (!entry) {
      return undefined
    }

    return entry.name && entry.name.trim() ? entry.name : ''
  }

  const pushCondition = (base, edgeLabel) => {
    if (edgeLabel === undefined) {
      conditions.push(base)
    } else if (edgeLabel === '') {
      // Cleared label must export as is:"", but omitEmpty strips empty strings —
      // mark it and restore in postProcessExport.
      conditions.push({ ...base, _emptyIs: true })
    } else {
      conditions.push({ ...base, is: edgeLabel })
    }
  }

  // Recursively walk a gateway's ancestor chain. Each gateway emits one condition;
  // we only push a non-gateway name to `follows` at the chain's terminal ancestor.
  const walkGatewayChain = (gatewayId, edgeLabel) => {
    const gw = context.gatewayMap.get(gatewayId)
    if (!gw) {
      return
    }

    for (const parentId of gw.parents) {
      if (parentId === 'start') {
        pushFollow('start')
        pushCondition({ after: 'start', if: gw.description }, edgeLabel)
        continue
      }

      const innerGateway = context.gatewayMap.get(parentId)
      if (innerGateway) {
        const innerEdgeLabel = edgeLabelFrom(gw.conditionLabels.find((cl) => cl.parentId === parentId))
        walkGatewayChain(parentId, innerEdgeLabel)
        pushCondition({ after: innerGateway.description, if: gw.description, _afterGatewayId: parentId }, edgeLabel)
      } else {
        const parentName = context.eventMap.get(parentId) || parentId
        pushFollow(parentId, parentName)
        pushCondition({ after: parentName, if: gw.description, _afterEventId: parentId }, edgeLabel)
      }
    }
  }

  for (const parentId of event.parents || []) {
    if (parentId === 'start') {
      pushFollow('start')
      continue
    }

    const gateway = context.gatewayMap.get(parentId)
    if (gateway) {
      const condLabel = event.conditionLabels?.find((cl) => cl.parentId === parentId)
      const condLabelName = condLabel ? edgeLabelFrom(condLabel) : (event.conditionLabel || undefined)
      walkGatewayChain(parentId, condLabelName)
    } else {
      const parentName = context.eventMap.get(parentId) || parentId
      pushFollow(parentId, parentName)
    }
  }

  const cleaned = omitEmpty({
    event: event.description,
    role: context.laneMap.get(event.laneId),
    group: context.groupMap.get(event.groupId),
    conditions: conditions.length > 0 ? conditions : undefined,
    follows: resolvedFollows.length > 0 ? resolvedFollows : undefined,
    command: commandCard
      ? context.schemaNameMap.get(commandCard.schemaId || context.virtualSchemaMap?.get(commandCard.id))
      : undefined,
    domainEventSchema: domainEventCard
      ? omitEmpty({
        name: context.schemaNameMap.get(domainEventCard.schemaId || context.virtualSchemaMap?.get(domainEventCard.id)) ||
          context.cardEntityNameMap?.get(domainEventCard.id),
        description: sanitizeCardDescription(domainEventCard.description || '') || undefined
      })
      : undefined,
    aggregateRoot: aggregateCard
      ? (context.schemaNameMap.get(aggregateCard.schemaId || context.virtualSchemaMap?.get(aggregateCard.id)) ||
        context.cardEntityNameMap?.get(aggregateCard.id))
      : undefined,
    readModels: readModels.length > 0 ? readModels : undefined,
    acceptanceCriteria,
    cards: customCards.length > 0 ? customCards : undefined
  }) || {}
  // Internal markers used by postProcessExport to resolve refs to the precise
  // event when multiple events share the same description. Stripped before serialization.
  cleaned._id = event.id
  if (resolvedFollowIds.length > 0) {
    cleaned._followsIds = resolvedFollowIds
  }

  return cleaned
}

export const buildDecisionsRegistry = (context, cardTypesMap = {}) => {
  const entries = []
  for (const [gatewayId, gw] of context.gatewayMap.entries()) {
    const role = gw.laneId ? context.laneMap.get(gw.laneId) : undefined
    const labelByParent = new Map(
      (gw.conditionLabels || []).map((cl) => [cl.parentId, cl.name])
    )

    // Special-role cards (Command, Aggregate Root, Read Model, Given-When-Then, Domain Event) on decisions are
    // intentionally dropped — decisions don't participate in the domain model. Only user-defined card types survive.
    const customCards = (gw.cards || [])
      .filter((c) => !cardTypesMap[c.cardTypeId]?.domainModelRole)
      .map((c) => omitEmpty({
        type: cardTypesMap[c.cardTypeId]?.title,
        description: decodeHtmlEntities(sanitizeCardDescription(c.description || ''))
      }))
      .filter(Boolean)

    let cardsEmitted = false

    for (const parentId of gw.parents.length > 0 ? gw.parents : ['start']) {
      let follows
      let conditionLabel
      let emptyConditionLabel = false
      let parentGatewayId
      let parentEventId

      if (parentId === 'start') {
        follows = 'start'
      } else if (context.gatewayMap.has(parentId)) {
        follows = context.gatewayMap.get(parentId).description
        if (labelByParent.has(parentId)) {
          const rawLabel = labelByParent.get(parentId)
          if (rawLabel && rawLabel.trim()) {
            conditionLabel = rawLabel
          } else {
            emptyConditionLabel = true
          }
        }
        parentGatewayId = parentId
      } else {
        follows = context.eventMap.get(parentId) || parentId
        parentEventId = parentId
      }

      const cards = !cardsEmitted && customCards.length > 0 ? customCards : undefined
      if (cards) {
        cardsEmitted = true
      }

      const entry = omitEmpty({
        follows,
        description: gw.description,
        conditionLabel,
        role,
        cards
      }) || {}

      entry._gatewayId = gatewayId
      if (parentGatewayId) {
        entry._parentGatewayId = parentGatewayId
      }
      if (parentEventId) {
        entry._followsEventId = parentEventId
      }
      if (emptyConditionLabel) {
        entry._emptyConditionLabel = true
      }
      entries.push(entry)
    }
  }

  return entries
}

export const mergeSchemaFields = (fields1, fields2) => {
  const merged = new Map()
  for (const field of fields1) {
    merged.set(field.name, { ...field })
  }
  for (const field of fields2) {
    if (merged.has(field.name)) {
      merged.set(field.name, { ...merged.get(field.name), ...field })
    } else {
      merged.set(field.name, { ...field })
    }
  }

  return [...merged.values()]
}

export const cleanupSchema = (schema, context) => {
  const { type, fields, entityId, domainEventIds, boundedContext, id, virtual, description, ...rest } = schema
  const name = context.schemaNameMap.get(id) || rest.name

  if (type === SchemaType.ENTITY) {
    // Key fields are implicitly required.
    const requiredNames = (fields || [])
      .filter((field) => field.tags?.includes('REQUIRED') || field.tags?.includes('KEY'))
      .map((field) => field.name)

    const keyNames = (fields || [])
      .filter((field) => field.tags?.includes('KEY'))
      .map((field) => field.name)

    const cleanedFields = (fields || []).map((field) => {
      return omitEmpty({
        name: field.name,
        description: field.description,
        array: field.cardinality === 'one-to-many' ? true : undefined,
        exampleData: field.exampleData,
        relatedEntity: field.relatedEntityId ? context.entityMap.get(field.relatedEntityId) : undefined,
        fields: cleanNestedFields(field.fields, context.entityMap),
        dataType: field.dataType
      })
    })

    // Entity = has an id field or a key; value objects have neither.
    const hasIdField = (fields || []).some((f) => f.name === 'id')
    const exportedType = hasIdField || keyNames.length > 0 ? 'Entity' : 'ValueObject'

    return omitEmpty({
      name,
      description: description || undefined,
      _type: virtual ? 'Entity' : exportedType,
      required: requiredNames.length > 0 ? requiredNames : undefined,
      key: keyNames.length > 0 ? keyNames : undefined,
      fields: virtual ? undefined : cleanedFields
    })
  }

  if (type === SchemaType.QUERY) {
    const cleanedFields = (fields || []).map((field) => {
      const isFilter = field.type === 'filter'

      return omitEmpty({
        name: field.name,
        description: field.description,
        relatedEntity: field.relatedEntityId ? context.entityMap.get(field.relatedEntityId) : undefined,
        fields: cleanNestedFields(field.fields, context.entityMap),
        isFilter: isFilter ? true : undefined,
        computed: field.computed ? true : undefined
      })
    })

    return omitEmpty({
      name,
      description: description || undefined,
      _type: type,
      entity: entityId ? context.entityMap.get(entityId) : undefined,
      array: schema.cardinality === 'one-to-many' ? true : undefined,
      fields: virtual ? undefined : cleanedFields
    })
  }

  if (type === SchemaType.DOMAIN_EVENT) {
    const cleanedFields = (fields || []).map((field) => {
      return omitEmpty({
        name: field.name,
        relatedEntity: field.relatedEntityId ? context.entityMap.get(field.relatedEntityId) : undefined,
        array: field.cardinality === 'one-to-many' ? true : undefined,
        fields: cleanNestedFields(field.fields, context.entityMap)
      })
    })

    return omitEmpty({
      name,
      _type: type,
      entity: entityId ? context.entityMap.get(entityId) : undefined,
      fields: virtual ? undefined : cleanedFields
    })
  }

  if (type === SchemaType.COMMAND) {
    const requiredNames = (fields || [])
      .filter((field) => field.tags?.includes('REQUIRED'))
      .map((field) => field.name)

    const cleanedFields = (fields || []).map((field) => {
      const nestedRequiredNames = (field.fields || [])
        .filter((f) => f.tags?.includes('REQUIRED'))
        .map((f) => f.name)

      return omitEmpty({
        name: field.name,
        relatedEntity: field.relatedEntityId ? context.entityMap.get(field.relatedEntityId) : undefined,
        array: field.cardinality === 'one-to-many' ? true : undefined,
        required: nestedRequiredNames.length > 0 ? nestedRequiredNames : undefined,
        fields: cleanNestedFields(field.fields, context.entityMap),
        hideInForm: field.hideInForm
      })
    })

    return omitEmpty({
      name,
      _type: type,
      required: requiredNames.length > 0 ? requiredNames : undefined,
      fields: virtual ? undefined : cleanedFields
    })
  }

  return omitEmpty({ name, _type: type, fields })
}

export const cleanupSchemas = (schemas, context) => {
  // Deduplicate by type+name, union-merging fields across duplicates
  const deduped = new Map()

  for (const schema of schemas) {
    const cleaned = cleanupSchema(schema, context)
    if (!cleaned || !cleaned.name) {
      continue
    }
    const key = `${cleaned._type}:${cleaned.name}`
    if (deduped.has(key)) {
      const existing = deduped.get(key)
      existing.fields = mergeSchemaFields(existing.fields || [], cleaned.fields || [])
    } else {
      deduped.set(key, { ...cleaned })
    }
  }

  const commands = []
  const queries = []
  const entities = []
  const valueObjects = []
  const domainEventSchemas = []

  for (const schema of deduped.values()) {
    const { _type, ...rest } = schema
    switch (_type) {
      case SchemaType.COMMAND:
        commands.push(rest)
        break
      case SchemaType.QUERY:
        queries.push(rest)
        break
      case SchemaType.DOMAIN_EVENT:
        domainEventSchemas.push(rest)
        break
      case 'ValueObject':
        valueObjects.push(rest)
        break
      case SchemaType.ENTITY:
        entities.push(rest)
        break
    }
  }

  return omitEmpty({
    commands: commands.length > 0 ? commands : undefined,
    queries: queries.length > 0 ? queries : undefined,
    domainEvents: domainEventSchemas.length > 0 ? domainEventSchemas : undefined,
    entities: entities.length > 0 ? entities : undefined,
    valueObjects: valueObjects.length > 0 ? valueObjects : undefined
  })
}

/**
 * Export a bounded context in a code-generation-friendly JSON format (version 1).
 *
 * Format highlights:
 * - Semantic domain event fields: command, aggregateRoot, readModels, acceptanceCriteria
 * - Custom card types preserved under cards[] with { type, description }
 * - DDD-role cards are promoted to named fields; all other cards go under cards[]
 * - Schemas organized into typed sections: { commands, queries, entities }
 * - Duplicate schemas with the same name are union-merged
 * - Roles (lanes) are a plain string array
 * - boundedContext is a top-level string
 * - Internal noise removed: cardTypeId, sortkey, done, bpmn:Task type, color, version on events/cards
 * - HTML entities decoded in text fields
 * - isRequired/isFilter only emitted when true
 */
export const buildWorkflowSpec = (workflow, boundedContext, cardTypesMap, includePositions = false) => {
  if (!workflow) {
    return ''
  }

  const domainEvents = workflow.domainEvents || []
  const allBoundedContexts = workflow.boundedContexts || []
  const currentBcId = boundedContext?.id || null

  // Create virtual schemas for command/readModel cards without a linked schema.
  // This lets the AI see that a card exists even though no fields are defined yet.
  const virtualSchemas = []
  const virtualSchemaMap = new Map()
  const cardEntityNameMap = new Map()
  for (const event of domainEvents) {
    for (const card of event.cards || []) {
      if (card.schemaId) {
        continue
      }
      const role = cardTypesMap[card.cardTypeId]?.domainModelRole
      if (role === DomainModelRole.COMMAND || role === DomainModelRole.READ_MODEL || role === DomainModelRole.DOMAIN_EVENT) {
        const name = sanitizeCardDescription(card.description || '')
        if (!name) {
          continue
        }

        // For domain events and read models (which have dedicated pages), check if a schema with that name already exists
        if (role === DomainModelRole.DOMAIN_EVENT || role === DomainModelRole.READ_MODEL) {
          const expectedType = role === DomainModelRole.DOMAIN_EVENT ? SchemaType.DOMAIN_EVENT : SchemaType.QUERY
          const existingSchema = (workflow.schemas || []).find((schema) =>
            schema.type === expectedType && schema.name.toLowerCase() === name.toLowerCase()
          )
          if (existingSchema) {
            cardEntityNameMap.set(card.id, existingSchema.name)
            continue
          }
        }

        const virtualId = `virtual:${card.id}`
        const typeMap = {
          [DomainModelRole.COMMAND]: SchemaType.COMMAND,
          [DomainModelRole.READ_MODEL]: SchemaType.QUERY,
          [DomainModelRole.DOMAIN_EVENT]: SchemaType.DOMAIN_EVENT
        }
        virtualSchemas.push({ id: virtualId, name, type: typeMap[role], virtual: true })
        virtualSchemaMap.set(card.id, virtualId)
      }
      if (role === DomainModelRole.AGGREGATE_ROOT) {
        const name = sanitizeCardDescription(card.description || '')
        if (!name) {
          continue
        }
        // Try to match against an existing entity by name
        const existingEntity = (workflow.schemas || []).find((schema) =>
          schema.type === SchemaType.ENTITY && schema.name.toLowerCase() === name.toLowerCase()
        )
        if (existingEntity) {
          // Entity exists — just record the name for the event output, don't create a virtual schema
          cardEntityNameMap.set(card.id, existingEntity.name)
        } else {
          const virtualId = `virtual:${card.id}`
          virtualSchemas.push({
            id: virtualId,
            name,
            type: 'Entity',
            virtual: true,
            boundedContext: card.boundedContext || null
          })
          virtualSchemaMap.set(card.id, virtualId)
        }
      }
    }
  }

  const schemas = [...(workflow.schemas || []), ...virtualSchemas]

  const context = buildExportContext(
    virtualSchemas.length > 0 ? { ...workflow, schemas } : workflow,
    cardTypesMap
  )
  context.virtualSchemaMap = virtualSchemaMap
  context.cardEntityNameMap = cardEntityNameMap

  const entitySchemas = schemas.filter((schema) => schema.type === SchemaType.ENTITY)
  const entityToBc = new Map()
  for (const entity of entitySchemas) {
    if (entity.boundedContext) {
      entityToBc.set(entity.id, entity.boundedContext)
    }
  }

  const getEventBcId = (event) => {
    const entityCard = (event.cards || []).find((card) => {
      const cardType = cardTypesMap[card.cardTypeId]
      return cardType?.domainModelRole === DomainModelRole.AGGREGATE_ROOT
    })
    if (!entityCard) {
      return null
    }
    // The entity schema's boundedContext is authoritative — it reflects the user's
    // explicit BC assignment in the domain model editor.
    // The card's boundedContext is a convenience copy that may be stale (e.g. set
    // to the active BC at card-creation time rather than the entity's actual BC),
    // so it is only used as a fallback when the schema has no BC set.
    return entityToBc.get(entityCard.schemaId) || entityCard.boundedContext || null
  }

  const collectReferencedSchemaIds = (bcEvents) => {
    const ids = new Set()
    for (const event of bcEvents) {
      for (const card of event.cards || []) {
        const virtualId = virtualSchemaMap.get(card.id)
        if (virtualId) {
          ids.add(virtualId)
        }
        if (card.schemaId) {
          ids.add(card.schemaId)
        }
        const schema = schemas.find((sch) => sch.id === card.schemaId)
        if (schema?.fields) {
          for (const field of schema.fields) {
            if (field.relatedEntityId) {
              ids.add(field.relatedEntityId)
            }
          }
        }
        if (schema?.entityId) {
          ids.add(schema.entityId)
        }
      }
    }
    return ids
  }

  const eventsByBc = new Map()
  const unassignedEvents = []
  for (const event of domainEvents) {
    if (event.type === 'bpmn:ExclusiveGateway') {
      continue
    }
    const bcId = getEventBcId(event)
    if (!bcId) {
      if (currentBcId === null) {
        if (!eventsByBc.has(null)) {
          eventsByBc.set(null, [])
        }
        eventsByBc.get(null).push(event)
      } else {
        unassignedEvents.push(event)
      }
      continue
    }
    if (!eventsByBc.has(bcId)) {
      eventsByBc.set(bcId, [])
    }
    eventsByBc.get(bcId).push(event)
  }

  // Current BC: events + all referenced schemas
  const currentBcEvents = eventsByBc.get(currentBcId) || []
  const currentBcEntityIds = new Set(
    entitySchemas
      .filter((entity) => currentBcId === null ? !entity.boundedContext : entity.boundedContext === currentBcId)
      .map((entity) => entity.id)
  )
  // Distribute standalone schemas (Query, DomainEvent) to BCs based on their entity's BC
  const standaloneSchemasByBc = new Map() // bcId → Set of schema ids

  const addStandaloneToBC = (bcId, schema) => {
    if (!standaloneSchemasByBc.has(bcId)) {
      standaloneSchemasByBc.set(bcId, new Set())
    }
    const set = standaloneSchemasByBc.get(bcId)
    set.add(schema.id)
    if (schema.entityId) {
      set.add(schema.entityId)
    }
    if (schema.fields) {
      for (const field of schema.fields) {
        if (field.relatedEntityId) {
          set.add(field.relatedEntityId)
        }
      }
    }
  }

  for (const schema of schemas) {
    if (schema.virtual || (schema.type !== SchemaType.QUERY && schema.type !== SchemaType.DOMAIN_EVENT)) {
      continue
    }

    if (schema.entityId) {
      const entity = schemas.find((sch) => sch.id === schema.entityId)
      if (entity?.boundedContext) {
        addStandaloneToBC(entity.boundedContext, schema)
      } else {
        addStandaloneToBC('__unassigned__', schema)
      }
    } else {
      addStandaloneToBC('__unassigned__', schema)
    }
  }

  // Build a set of all schema IDs claimed by any specific BC (not unassigned)
  // to prevent duplication across BCs
  const allClaimedByBc = new Set()
  for (const [bcId, idSet] of standaloneSchemasByBc) {
    if (bcId !== '__unassigned__') {
      for (const id of idSet) {
        allClaimedByBc.add(id)
      }
    }
  }

  const currentReferencedIds = collectReferencedSchemaIds(currentBcEvents)
  for (const id of currentBcEntityIds) {
    currentReferencedIds.add(id)
  }
  for (const id of standaloneSchemasByBc.get(currentBcId) || []) {
    currentReferencedIds.add(id)
  }
  const currentSchemas = schemas.filter((schema) => currentReferencedIds.has(schema.id))

  // External BCs: events + all referenced schemas (entities, commands, queries)
  const externalBcExports = allBoundedContexts
    .filter((bc) => bc.id !== currentBcId && bc.name !== null)
    .map((bc) => {
      const bcEntityIds = new Set(
        entitySchemas.filter((entity) => entity.boundedContext === bc.id).map((entity) => entity.id)
      )
      const bcEvents = eventsByBc.get(bc.id) || []
      const bcReferencedIds = collectReferencedSchemaIds(bcEvents)
      for (const id of bcEntityIds) bcReferencedIds.add(id)
      for (const id of standaloneSchemasByBc.get(bc.id) || []) bcReferencedIds.add(id)
      // Only include schemas that belong to this BC or have no BC — don't duplicate schemas claimed by other BCs
      const bcStandaloneIds = standaloneSchemasByBc.get(bc.id) || new Set()
      const bcSchemas = schemas.filter(
        (schema) => bcReferencedIds.has(schema.id) &&
          (!schema.boundedContext || schema.boundedContext === bc.id) &&
          (!allClaimedByBc.has(schema.id) || bcStandaloneIds.has(schema.id))
      )
      const cleanedSchemas = cleanupSchemas(bcSchemas, context)

      const cleanedEvents = bcEvents.map((event) =>
        cleanupDomainEvent(event, cardTypesMap, context)
      )

      return omitEmpty({
        name: bc.name,
        domainEvents: cleanedEvents.length > 0 ? cleanedEvents : undefined,
        schemas: cleanedSchemas
      })
    })
    .filter(Boolean)
    .filter(
      (bc) =>
        bc.domainEvents?.length ||
        bc.schemas?.entities?.length ||
        bc.schemas?.commands?.length ||
        bc.schemas?.queries?.length ||
        bc.schemas?.domainEvents?.length
    )

  const unassignedEntityIds = new Set(
    entitySchemas.filter((entity) => !entity.boundedContext).map((entity) => entity.id)
  )
  const unassignedReferencedIds = collectReferencedSchemaIds(unassignedEvents)
  for (const id of unassignedEntityIds) {
    unassignedReferencedIds.add(id)
  }
  for (const id of standaloneSchemasByBc.get('__unassigned__') || []) {
    unassignedReferencedIds.add(id)
  }
  // Only include schemas that have no BC, are not claimed by any specific BC,
  // and are not already included in the current BC's schemas
  const unassignedSchemas = schemas.filter(
    (schema) => unassignedReferencedIds.has(schema.id) &&
      !schema.boundedContext &&
      !allClaimedByBc.has(schema.id) &&
      !currentReferencedIds.has(schema.id)
  )
  const cleanedUnassignedSchemas = cleanupSchemas(unassignedSchemas, context)

  if (unassignedEvents.length > 0 || cleanedUnassignedSchemas) {
    const unassignedExport = omitEmpty({
      name: 'Unassigned',
      domainEvents: unassignedEvents.map((event) =>
        cleanupDomainEvent(event, cardTypesMap, context)
      ).filter(Boolean),
      schemas: cleanedUnassignedSchemas
    })

    if (unassignedExport) {
      externalBcExports.push(unassignedExport)
    }
  }

  const cleanedLanes = (workflow.lanes || []).map((lane) => lane.name)
  const cleanedGroups = (workflow.groups || []).map((group) =>
    omitEmpty({ name: group.name, startSlotX: group.startSlotX })
  )

  const decisions = buildDecisionsRegistry(context, cardTypesMap)

  const result = omitEmpty({
    $schema: DOMAIN_MODEL_SCHEMA_URL,
    version: 1,
    boundedContext: boundedContext?.name ?? 'Unassigned',
    roles: cleanedLanes.length > 0 ? cleanedLanes : undefined,
    domainEvents: currentBcEvents.map((event) =>
      cleanupDomainEvent(event, cardTypesMap, context)
    ),
    decisions: decisions.length > 0 ? decisions : undefined,
    schemas: cleanupSchemas(currentSchemas, context),
    externalBoundedContexts: externalBcExports.length > 0 ? externalBcExports : undefined,
    groups: cleanedGroups,
    releases: workflow.releases || []
  })

  let positionsById = {}

  if (includePositions) {
    const laneIdSet = new Set((workflow.lanes || []).map((lane) => lane.id))
    const canPosition = laneIdSet.size > 0 &&
      (workflow.domainEvents || []).every((event) => event.laneId && laneIdSet.has(event.laneId))

    if (canPosition) {
      positionsById = computeGridPositions({
        domainEvents: workflow.domainEvents || [],
        lanes: workflow.lanes || [],
        groups: workflow.groups || []
      })
    }
  }

  postProcessExport(result, positionsById)

  return JSON.stringify(result, null, 2)
}

export const buildCardTypesMap = (cardTypes) => {
  return Object.fromEntries((cardTypes || []).map((ct) => [ct.id, ct]))
}

export const parseReadModelFields = (fields) => {
  if (!fields || !Array.isArray(fields)) {
    return []
  }

  return fields.map((field) => {
    const schemaField = {
      name: field.name,
      cardinality: field.cardinality,
      tags: field.indexed ? ['INDEXED'] : []
    }
    if (field.description) {
      schemaField.description = field.description
    }
    if (field.computed) {
      schemaField.computed = true
    }
    if (field.fields?.length > 0) {
      schemaField.fields = parseReadModelFields(field.fields)
    }

    return schemaField
  })
}

export const parseDomainEventFields = (fields) => {
  if (!fields || !Array.isArray(fields)) {
    return []
  }

  return fields.map((field) => {
    const schemaField = { name: field.name }
    if (field.cardinality) {
      schemaField.cardinality = field.cardinality
    }
    if (field.fields?.length > 0) {
      schemaField.fields = parseDomainEventFields(field.fields)
    }

    return schemaField
  })
}

export const parseCommandFields = (eventData) => {
  const requiredSet = new Set(eventData.required || [])

  const parseFields = (fields) => {
    return (fields || []).map((field) => {
      const schemaField = { name: field.name }
      if (requiredSet.has(field.name)) {
        schemaField.tags = ['REQUIRED']
      }
      if (field.array === true) {
        schemaField.cardinality = 'one-to-many'
      }
      if (field.fields?.length > 0) {
        schemaField.fields = parseFields(field.fields)
      }
      return schemaField
    })
  }

  return parseFields(eventData.fields)
}

export const parseEntityResponse = (specEntity, entityNameToIdMap = new Map()) => {
  const requiredSet = new Set(specEntity.required || [])
  const keySet = new Set(specEntity.key || [])

  const fields = (specEntity.fields || []).map((field) => {
    let relatedEntityId = null
    if (field.relatedEntity && entityNameToIdMap) {
      relatedEntityId = entityNameToIdMap.get(field.relatedEntity.toLowerCase()) || null
    }

    const isKey = keySet.has(field.name)
    const tags = []
    // A key is always required.
    if (requiredSet.has(field.name) || isKey) {
      tags.push('REQUIRED')
    }
    if (isKey) {
      tags.push('KEY')
    }

    return {
      name: field.name,
      description: field.description || undefined,
      cardinality: field.array ? 'one-to-many' : 'one-to-one',
      exampleData: field.exampleData || [],
      tags: tags.length > 0 ? tags : undefined,
      dataType: field.dataType || 'string',
      relatedEntityId
    }
  })

  return {
    name: specEntity.name,
    description: specEntity.description || undefined,
    type: 'Entity',
    fields
  }
}

/**
 * Resolve the bounded context names the AI assigned to each entity (carried on the
 * raw LLM responses) into { id, name } objects, and map every entity schema to its
 * bounded context id. Names are deduped case-insensitively, existing workflow
 * bounded contexts are reused by name, and entities with no assigned name fall back
 * to `defaultName` (a single bounded context, as before).
 * Returns { boundedContexts, entityBcId: Map<schemaId, bcId> }.
 */
export const assignBoundedContexts = (entitySchemas, llmEntities, defaultName, generateId, existingBoundedContexts = []) => {
  const bcNameByEntity = new Map(
    (llmEntities || [])
      .filter((entity) => entity && entity.name && entity.boundedContext)
      .map((entity) => [entity.name.toLowerCase(), String(entity.boundedContext).trim()])
  )

  const boundedContexts = [...(existingBoundedContexts || [])]
  const bcIdByName = new Map(
    boundedContexts
      .filter((bc) => bc && bc.name)
      .map((bc) => [bc.name.trim().toLowerCase(), bc.id])
  )

  const ensureBc = (rawName) => {
    const name = (rawName || '').trim() || defaultName
    const key = name.toLowerCase()
    if (bcIdByName.has(key)) {
      return bcIdByName.get(key)
    }

    const bc = { id: generateId(), name }
    boundedContexts.push(bc)
    bcIdByName.set(key, bc.id)

    return bc.id
  }

  const entityBcId = new Map()
  for (const entity of entitySchemas || []) {
    const bcName = bcNameByEntity.get((entity.name || '').toLowerCase())
    entityBcId.set(entity.id, ensureBc(bcName))
  }

  return { boundedContexts, entityBcId }
}
